// components/avances/AnnulationPrivilegieeDialog.jsx
// ============================================================================
// ANNULATION PRIVILÉGIÉE D'UNE AVANCE
// ============================================================================
// Porte de secours quand le gestionnaire qui a accordé l'avance ne peut plus
// l'annuler. La fenêtre montre pourquoi le chemin ordinaire est bloqué, fait
// choisir ce qui s'est passé avec l'argent, et envoie une demande au Centre de
// Support : une autre personne la valide, et tout est journalisé.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Info, Loader2, Lock, ShieldAlert } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import { annulationPrivilegieeApi, usePermissionsAnnulationPrivilegiee } from './annulationPrivilegieeApi';

const BOUTON_SECONDAIRE = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_PRINCIPAL = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md bg-[#246990] px-4 text-sm font-medium text-white transition hover:bg-[#1f5a7a] disabled:cursor-not-allowed disabled:opacity-50';

const formaterMontant = (montant) => `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`;
const formaterDate = (valeur) => {
  const [annee, mois, jour] = String(valeur || '').slice(0, 10).split('-');
  return annee && mois && jour ? `${jour}/${mois}/${annee}` : '-';
};

const CAS = [
  {
    valeur: 'jamais_remis',
    titre: "L'argent n'a jamais été remis",
    texte: "Avance saisie par erreur, ou accordée hors caisse. Elle est annulée sans mouvement d'argent."
  },
  {
    valeur: 'rendu',
    titre: "L'employé a rendu l'argent",
    texte: "Un « retour d'avance » entre dans la caisse du jour de la personne qui validera la demande."
  }
];

const AnnulationPrivilegieeDialog = ({ avanceId, open, onClose, onEnvoyee }) => {
  // Un super validateur pourra valider lui-même : sa justification doit alors
  // tenir seule (même règle côté serveur).
  const { estSuperValidateur } = usePermissionsAnnulationPrivilegiee();
  const longueurMinimale = estSuperValidateur ? 30 : 10;
  const [apercu, setApercu] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [cas, setCas] = useState('');
  const [justification, setJustification] = useState('');
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!open || !avanceId) return undefined;
    let annule = false;
    setChargement(true);
    annulationPrivilegieeApi.apercu(avanceId)
      .then((donnees) => {
        if (annule) return;
        setApercu(donnees);
        if (donnees.cas_autorises?.length === 1) setCas(donnees.cas_autorises[0]);
      })
      .catch((error) => { if (!annule) setErreur(error.message); })
      .finally(() => { if (!annule) setChargement(false); });
    return () => { annule = true; };
  }, [open, avanceId]);

  const bloquants = apercu?.bloquants || [];
  const peutEnvoyer = Boolean(apercu) && bloquants.length === 0 && Boolean(cas)
    && justification.trim().length >= longueurMinimale && !envoi;

  const envoyer = async () => {
    if (!peutEnvoyer) return;
    setEnvoi(true);
    setErreur('');
    try {
      const resultat = await annulationPrivilegieeApi.demander(avanceId, { cas, justification: justification.trim() });
      onEnvoyee?.(resultat);
    } catch (error) {
      setErreur(error.message);
    } finally {
      setEnvoi(false);
    }
  };

  const avance = apercu?.avance;

  return (
    <Dialog open={open} onOpenChange={(ouvert) => { if (!ouvert && !envoi) onClose?.(); }}>
      <DialogContent className="w-[95vw] max-w-4xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <ShieldAlert size={18} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">Annulation privilégiée d'une avance</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Validée au Centre de Support par une autre personne, et inscrite au journal des actions privilégiées.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto bg-gray-50 px-5 py-4">
          {chargement ? (
            <div className="flex h-48 items-center justify-center">
              <Loader2 className="animate-spin text-[#246990]" size={28} />
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="space-y-4">
                {erreur && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{erreur}</div>
                )}

                {bloquants.length > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-800">
                      <Lock size={16} />
                      Même par privilège, cette avance ne peut pas être annulée
                    </div>
                    <ul className="space-y-1.5">
                      {bloquants.map((texte) => (
                        <li key={texte} className="text-sm text-red-700">{texte}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {avance && bloquants.length === 0 && (
                  <>
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <p className="mb-3 text-sm font-semibold text-gray-900">Ce qui s'est passé avec l'argent</p>
                      <div className="grid gap-3 md:grid-cols-2">
                        {CAS.map((option) => {
                          const autorise = apercu.cas_autorises.includes(option.valeur);
                          const choisi = cas === option.valeur;
                          return (
                            <button
                              key={option.valeur}
                              type="button"
                              disabled={!autorise}
                              onClick={() => setCas(option.valeur)}
                              className={`rounded-lg border p-3 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${choisi
                                ? 'border-[#246990] bg-[#eef7fb] ring-2 ring-[#246990]/15'
                                : 'border-gray-200 bg-white hover:border-gray-300'}`}
                            >
                              <span className="block text-sm font-semibold text-gray-900">{option.titre}</span>
                              <span className="mt-1 block text-xs text-gray-500">{option.texte}</span>
                            </button>
                          );
                        })}
                      </div>
                      {apercu.sortie && (
                        <p className="mt-3 text-xs text-gray-500">
                          Une sortie de caisse a été enregistrée le {formaterDate(apercu.sortie.date)} dans la caisse de {apercu.sortie.gestionnaire || '?'} :
                          l'argent est donc parti, seul le retour est possible. Cette sortie reste intacte.
                        </p>
                      )}
                    </div>

                    <label className="block rounded-lg border border-gray-200 bg-white p-4">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Justification</span>
                      <textarea
                        rows={4}
                        value={justification}
                        onChange={(event) => setJustification(event.target.value)}
                        placeholder="Pourquoi le gestionnaire ne peut pas l'annuler lui-même, et ce qui a été constaté."
                        className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                      />
                      <span className="mt-1 block text-[11px] text-gray-500">
                        {longueurMinimale} caractères au moins ({justification.trim().length} saisis). {estSuperValidateur
                          ? 'Si vous validez vous-même, personne d\'autre ne la relira : elle doit se suffire à elle-même.'
                          : 'Elle est lue par le validateur et conservée au journal.'}
                      </span>
                    </label>
                  </>
                )}
              </div>

              {avance && (
                <aside className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <p className="mb-3 text-sm font-semibold text-gray-900">L'avance</p>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between gap-3"><dt className="text-gray-500">Employé</dt><dd className="text-right font-medium text-gray-900">{avance.employe}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-gray-500">Salaire de</dt><dd className="text-right text-gray-900">{avance.mois || '-'}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-gray-500">Demandée le</dt><dd className="text-right text-gray-900">{formaterDate(avance.date_demande)}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-gray-500">Accordée par</dt><dd className="text-right text-gray-900">{avance.createur}</dd></div>
                      <div className="flex justify-between gap-3 border-t pt-2"><dt className="text-gray-500">Montant</dt><dd className="text-right font-semibold text-gray-900">{formaterMontant(avance.montant)}</dd></div>
                    </dl>
                  </div>

                  {apercu.raisons_blocage_ordinaire?.length > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
                        <AlertTriangle size={15} />
                        Pourquoi l'annulation ordinaire est bloquée
                      </p>
                      <ul className="space-y-1 text-sm text-amber-800">
                        {apercu.raisons_blocage_ordinaire.map((texte) => <li key={texte}>{texte}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-start gap-2 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-sm text-[#246990]">
                    <Info size={15} className="mt-0.5 shrink-0" />
                    <span>
                      {estSuperValidateur
                        ? 'La demande part au Centre de Support. En tant que super validateur, vous pourrez la valider vous-même avec votre code de double authentification.'
                        : 'La demande part au Centre de Support. Vous ne pourrez pas la valider vous-même.'}
                    </span>
                  </div>
                </aside>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
          <button type="button" onClick={onClose} disabled={envoi} className={BOUTON_SECONDAIRE}>
            Annuler
          </button>
          <button type="button" onClick={envoyer} disabled={!peutEnvoyer} className={BOUTON_PRINCIPAL}>
            {envoi ? <Loader2 className="animate-spin" size={16} /> : <ShieldAlert size={16} />}
            Envoyer la demande
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnnulationPrivilegieeDialog;
